import { useEffect, useState } from "react";
import { format } from "date-fns";
import { PenSquare, TrendingUp, Clock, MessageSquare, Send, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, categoryLabel } from "../lib/categories";
import OpinionCard from "../components/OpinionCard";
import CommentThread from "../components/CommentThread";
import Reveal from "../components/Reveal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const SORTS = [
  { key: "trending", label: "Trending", icon: TrendingUp },
  { key: "latest", label: "Latest", icon: Clock },
  { key: "controversial", label: "Most Debated", icon: MessageSquare },
];

export default function VoicesPage() {
  const { user, openAuth } = useAuth();
  const [sort, setSort] = useState("trending");
  const [cat, setCat] = useState("all");
  const [items, setItems] = useState(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState("");
  const [form, setForm] = useState({ title: "", category: "ideas", body: "", image_url: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const load = () => {
    setItems(null);
    const params = { sort };
    if (cat !== "all") params.category = cat;
    api.get("/opinions", { params }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(load, [sort, cat]);

  useEffect(() => {
    if (selected) {
      api.get(`/opinions/${selected.id}/comments`).then((r) => setComments(r.data)).catch(() => {});
      setDraft("");
    }
  }, [selected]);

  const openSubmit = () => {
    if (!user) { openAuth("register"); return; }
    setSubmitOpen(true);
  };

  const submitOpinion = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/opinions", form);
      toast.success("Your voice is live. Let the debate begin.");
      setSubmitOpen(false);
      setForm({ title: "", category: "ideas", body: "", image_url: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!draft.trim() || !selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/opinions/${selected.id}/comments`, { body: draft.trim() });
      setComments((c) => [...c, data]);
      setDraft("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="voices-page" className="max-w-[1440px] mx-auto px-5 lg:px-10 py-16 lg:py-24">
      <Reveal>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-crimson mb-4">Community Voices</p>
            <h1 className="font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              Your take.<br /><span className="text-outline break-words">Their debate.</span>
            </h1>
            <p className="text-ash mt-6 max-w-xl leading-relaxed">
              This is your space. Publish an opinion on anything we cover, vote on the takes
              that land, and argue it out in the comments. The best voices rise to the top.
            </p>
          </div>
          <button data-testid="submit-opinion-open-modal" onClick={openSubmit}
            className="shrink-0 bg-crimson text-porcelain font-mono text-xs uppercase tracking-[0.18em] font-medium px-7 py-4 rounded-full flex items-center gap-2 hover:bg-kinetic hover:text-obsidian transition-colors duration-300 w-fit">
            <PenSquare size={15} /> Share your take
          </button>
        </div>
      </Reveal>

      <Reveal>
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-10">
          <div className="flex gap-2 flex-wrap">
            {SORTS.map((s) => (
              <button key={s.key} data-testid={`voices-sort-tab-${s.key}`} onClick={() => setSort(s.key)}
                className={`flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] px-4 py-2.5 rounded-full border transition-colors duration-300 ${sort === s.key ? "bg-crimson border-crimson text-porcelain font-medium" : "border-white/10 text-ash hover:text-porcelain"}`}>
                <s.icon size={13} /> {s.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <button onClick={() => setCat("all")} data-testid="voices-category-all"
              className={`whitespace-nowrap text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-2 rounded-full border transition-colors ${cat === "all" ? "border-kinetic text-kinetic" : "border-white/10 text-ash hover:text-porcelain"}`}>
              All topics
            </button>
            {CATEGORIES.map((c) => (
              <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`voices-category-${c.slug}`}
                className={`whitespace-nowrap text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-2 rounded-full border transition-colors ${cat === c.slug ? "border-kinetic text-kinetic" : "border-white/10 text-ash hover:text-porcelain"}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      {!items ? (
        <p className="font-mono text-ash text-xs uppercase tracking-[0.3em] py-24 text-center animate-pulse">Loading voices…</p>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/15 py-24 text-center">
          <p className="font-mono text-ash text-xs uppercase tracking-[0.3em]">Silence in this corner. Say the first thing.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((o, i) => (
            <OpinionCard key={o.id} opinion={o} index={i} onOpen={setSelected} />
          ))}
        </div>
      )}

      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent data-testid="submit-opinion-modal" className="bg-surface border-white/10 text-porcelain sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase tracking-tight text-2xl">Share your take</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitOpinion} className="flex flex-col gap-3">
            <input data-testid="opinion-input-title" value={form.title} required minLength={5} maxLength={140}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Your headline — make it punchy"
              className="bg-obsidian border border-white/15 focus:border-crimson/60 outline-none px-4 py-3.5 text-sm transition-colors" />
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button type="button" key={c.slug} data-testid={`opinion-category-${c.slug}`} onClick={() => setForm({ ...form, category: c.slug })}
                  className={`text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border transition-colors ${form.category === c.slug ? "bg-kinetic text-obsidian border-kinetic" : "border-white/10 text-ash hover:text-porcelain"}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <div>
              {form.image_url ? (
                <div className="relative border border-white/10">
                  <img src={form.image_url} alt="Your upload" className="w-full max-h-48 object-cover" />
                  <button type="button" data-testid="opinion-photo-remove" onClick={() => setForm({ ...form, image_url: "" })}
                    className="absolute top-2 right-2 bg-obsidian/80 backdrop-blur px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-crimson border border-crimson/40 hover:bg-crimson hover:text-porcelain transition-colors">
                    Remove
                  </button>
                </div>
              ) : (
                <label data-testid="opinion-photo-upload-label"
                  className={`flex flex-col items-center justify-center gap-2.5 border border-dashed py-8 cursor-pointer transition-colors ${uploading ? "border-kinetic/50 text-kinetic" : "border-white/20 text-ash hover:border-crimson/60 hover:text-porcelain"}`}>
                  <ImagePlus size={20} />
                  <span className="text-[11px] font-mono uppercase tracking-[0.2em]">
                    {uploading ? "Uploading photo…" : "Add a photo — your ink, your wall, your ride"}
                  </span>
                  <span className="text-[10px] font-mono text-ash/60">JPG, PNG, GIF or WebP · max 8 MB</span>
                  <input data-testid="opinion-photo-upload-input" type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
                </label>
              )}
            </div>
            <textarea data-testid="opinion-input-content" value={form.body} required minLength={10} maxLength={4000} rows={6}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Make your case. Strong opinions, loosely held."
              className="bg-obsidian border border-white/15 focus:border-crimson/60 outline-none px-4 py-3.5 text-sm leading-relaxed resize-y transition-colors" />
            <button data-testid="opinion-submit-confirm-btn" type="submit" disabled={busy}
              className="bg-crimson text-porcelain font-mono text-xs uppercase tracking-[0.18em] font-medium py-4 rounded-full hover:bg-kinetic hover:text-obsidian transition-colors duration-300 disabled:opacity-50 mt-1">
              {busy ? "Publishing…" : "Publish your voice"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="opinion-detail-modal" className="bg-surface border-white/10 text-porcelain sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-crimson">{categoryLabel(selected.category)}</span>
                <button onClick={() => setSelected(null)} className="text-ash hover:text-porcelain"><X size={18} /></button>
              </div>
              <h2 className="font-display font-extrabold text-2xl sm:text-3xl leading-tight tracking-tight">{selected.title}</h2>
              <p className="text-[11px] font-mono text-ash uppercase tracking-[0.15em] mt-3">@{selected.author_name} · {format(new Date(selected.created_at), "dd MMM yyyy")}</p>
              {selected.image_url && (
                <div className="mt-5 border border-white/10 overflow-hidden">
                  <img src={selected.image_url} alt={selected.title} className="w-full max-h-72 object-cover" />
                </div>
              )}
              <p className="text-porcelain/85 leading-relaxed mt-6">{selected.body}</p>

              <div className="border-t border-white/10 mt-8 pt-6">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-kinetic mb-5">The debate ({comments.length})</p>
                {user ? (
                  <form onSubmit={postComment} className="flex gap-2 mb-6">
                    <input data-testid="opinion-comment-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                      placeholder="Respond to this take…" maxLength={2000}
                      className="flex-1 bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3 text-sm transition-colors" />
                    <button data-testid="opinion-comment-submit-button" type="submit" disabled={busy || !draft.trim()}
                      className="bg-kinetic text-obsidian px-4 flex items-center justify-center hover:bg-porcelain transition-colors disabled:opacity-50">
                      <Send size={15} />
                    </button>
                  </form>
                ) : (
                  <button data-testid="opinion-comment-signin" onClick={() => { setSelected(null); openAuth("login"); }}
                    className="w-full border border-white/10 text-ash hover:text-kinetic hover:border-kinetic/40 text-xs font-mono uppercase tracking-[0.18em] py-3.5 mb-6 transition-colors">
                    Sign in to join this debate
                  </button>
                )}
                <CommentThread
                  comments={comments}
                  user={user}
                  openAuth={openAuth}
                  busy={busy}
                  onFlame={async (c) => {
                    if (!user) { openAuth("login"); return; }
                    try {
                      const { data } = await api.post(`/opinions/comments/${c.id}/flame`);
                      setComments((cs) => cs.map((x) => x.id === c.id ? { ...x, flamed: data.flamed, flames: data.flames } : x));
                    } catch (err) {
                      toast.error(formatApiError(err));
                    }
                  }}
                  onSubmit={async (parentId, body) => {
                    try {
                      const { data } = await api.post(`/opinions/${selected.id}/comments`, { body, parent_id: parentId });
                      setComments((c) => [...c, data]);
                      load();
                      return true;
                    } catch (err) {
                      toast.error(formatApiError(err));
                      return false;
                    }
                  }}
                />
                {comments.length === 0 && (
                  <p className="text-ash font-mono text-xs uppercase tracking-[0.2em] py-4 text-center">No responses yet — go first.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
